import React, { useState, useRef, useEffect } from "react";
import { Trash2 } from "lucide-react";
import ReactCrop, { PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useTranslation } from "react-i18next";
import { base64ToFile, sanitizeFileName } from "../helpers";

const MAX_SIZE = 3 * 1024 * 1024; // 3MB

interface ImageUploadProps {
  selectedImageId: string;
  setSelectedImageId: (id: string) => void;
  onChange: (fileName: string | null, file: any) => void;
}

const ImageUpload = ({ selectedImageId, setSelectedImageId, onChange }: ImageUploadProps) => {
  const { t } = useTranslation();

  const [src, setSrc] = useState<string | null>(null);
  const defaultCrop: PixelCrop = {
    unit: "px",
    width: 1080,
    height: 1080,
    x: 1080,
    y: 1080,
  };
  const [crop, setCrop] = useState<PixelCrop>(defaultCrop);
  const [fileNameImage, setFileNameImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const originFileNameImage = useRef<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (fileNameImage && croppedImage && croppedImage.length <= MAX_SIZE) {
      onChange(fileNameImage, base64ToFile(croppedImage, fileNameImage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedImage, fileNameImage]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSrc(ev.target?.result as string);
        originFileNameImage.current = file.name;
      };
      reader.readAsDataURL(file);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSrc(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoaded = (img: HTMLImageElement) => {
    imgRef.current = img;
    const defaultCrop = {
      unit: "px",
      width: img.width,
      height: img.height,
      x: 0,
      y: 0,
    };
    setCrop(defaultCrop as PixelCrop);
    setTimeout(() => {
      makeClientCrop(defaultCrop as PixelCrop);
    }, 0);
  };

  const makeClientCrop = async (crop: PixelCrop) => {
    if (originFileNameImage.current && imgRef.current && crop.width && crop.height) {
      const base64 = await compressCanvas(imgRef.current, crop, MAX_SIZE);
      if (base64) {
        setCroppedImage(base64);
        const fileName = sanitizeFileName(Date.now() + "-" + originFileNameImage.current);
        setFileNameImage(fileName);
        setSelectedImageId(fileName);
      }
    }
  };

  const compressCanvas = async (image: HTMLImageElement, crop: PixelCrop, maxSizeBytes: number) => {
    let quality = 0.92;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    let width = crop.width * scaleX;
    let height = crop.height * scaleY;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No canvas");

    while (true) {
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );

      const base64 = canvas.toDataURL("image/jpeg", quality);
      const sizeBytes = Math.ceil((base64.length * 3) / 4);

      if (sizeBytes <= maxSizeBytes) return base64;

      if (quality > 0.5) {
        quality -= 0.07;
      } else {
        width *= 0.9;
        height *= 0.9;
      }

      if (width < 1080 || height < 1080) return base64;
    }
  };

  const renderCroppedImage = () => {
    if (src) {
      return (
        <div className="w-full mb-4 flex flex-col items-center rounded-lg">
          <div
            className={`w-120 h-120 flex flex-col items-center justify-center rounded-lg bg-gray-100 cursor-pointer 
                ${selectedImageId === fileNameImage ? "border-2 border-blue-500 shadow-md shadow-blue-500" : ""}`}
            onClick={() => {
              setSelectedImageId(fileNameImage);
            }}
          >
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={makeClientCrop}
            >
              <img
                src={src}
                onLoad={(e) => onImageLoaded(e.currentTarget as HTMLImageElement)}
                alt="image"
                className="max-w-full max-h-full bg-white"
              />
            </ReactCrop>
          </div>
          <div className="w-full mt-2 flex items-center justify-center gap-2">
            <div
              className="bg-red-500 flex items-center gap-1 px-1.5 py-1 rounded-md cursor-pointer"
              onClick={() => {
                setSrc(null);
                setCrop(defaultCrop);
                setFileNameImage(null);
                setCroppedImage(null);
                originFileNameImage.current = null;
                imgRef.current = null;
                onChange(null, null);
              }}
            >
              <Trash2 className="w-2.5 h-2.5 text-white" />
              <span className="text-xs font-medium text-white">{t("Remove")}</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        className="w-50 h-50 flex flex-col items-center justify-center py-2 mx-4 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 cursor-pointer transition hover:border-blue-400 mb-3"
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => document.getElementById("image-upload-input")?.click()}
      >
        <input
          id="image-upload-input"
          type="file"
          accept="image/*"
          onChange={onFileChange}
          className="hidden"
        />
        <div className="p-4 text-sm text-gray-500 text-center">
          {t("Drop a file to upload or click to select")}
        </div>
      </div>
    );
  };

  return <>{renderCroppedImage()}</>;
};

export default ImageUpload;
